import React, { useEffect, useState, useCallback } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import {
  isScrollingDisabled,
  setDOMLoaded,
  setFirstLoaded,
  manageDisableScrolling,
} from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import { useIframeDetection } from '../../hooks/useIframeDetection';

import css from './StorePage.module.css';
import { parse, stringify } from '../../util/urlHelpers';
import { listingMatchesCondition } from '../../util/listingCondition';
import { listingMatchesDeliveryOptions } from '../../util/listingDeliveryOptions';
import { isWarehouseChannel, isWarehouseSource } from '../../util/itemSourceChannel';
import { parseSelectFilterOptions } from '../../util/search';
import { normalizeLegacySubcategorySlug } from '../../util/storeTaxonomy';
import { useHistory, useLocation } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { fetchCreators } from '../../ducks/user.duck';
import { reloadListings } from './StorePage.duck';
import { sanitizeTaxonomyFilterParams } from '../SearchPage/SearchPage.shared';
import SearchPagination from '../LandingPage/sections/SearchPagination';

import CatalogSearchContent from '../../components/CatalogSearchContent/CatalogSearchContent';
import { BRANDS } from '../../config/brandRegistry';
import { filterListingsByBrand, getAvailableBrandValues } from '../../util/brandAvailability';

// Sort options for the store
const SORT_OPTIONS = [
  { key: 'createdAt', label: 'Newest' },
  { key: '-createdAt', label: 'Oldest' },
  { key: '-price', label: 'Lowest Price' },
  { key: 'price', label: 'Highest Price' },
];
const ITEMS_PER_PAGE = 20;

export const getSanitizedStoreUrlParams = search => sanitizeTaxonomyFilterParams(parse(search));

const StorePageComponent = props => {
  const config = useConfiguration();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const {
    scrollingDisabled,
    userShowError,
    user,
    intl,
    currentCategory,
    categories,
    subcategories,
    currentSubcategory,
    handleSearch,
    listings,
    fetchingListings,
    manufacturerImages,
    manufacturerImagesInProgress,
    fallbackStoreId,
  } = props;

  const [searchKeyword, setSearchKeyword] = useState(parse(location.search).keywords || '');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const isInIframe = useIframeDetection();
  const rawUrlQueryParams = parse(location.search);
  const urlQueryParams = getSanitizedStoreUrlParams(location.search);
  const rawQueryString = stringify(rawUrlQueryParams);
  const sanitizedQueryString = stringify(urlQueryParams);
  const sanitizedSearch = sanitizedQueryString ? `?${sanitizedQueryString}` : '';

  const profileUser = ensureUser(user);
  const { displayName, publicData } = profileUser?.attributes?.profile || {};
  const { greetingHeading } = publicData || {};

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);
  let profileTitle = 'Loading store';
  if (displayName) {
    profileTitle = intl.formatMessage({ id: 'ProfilePage.desktopHeading' }, { name: displayName });
    if (greetingHeading) {
      profileTitle = greetingHeading.replace('{name}', displayName);
    }
  }

  // An explicit `/store/:id` is a declared intent, so it is never overridden while it is viewable.
  // `loadData` sets `fallbackStoreId` only after the requested store proved unusable and an
  // approved store was found for the same business domain. `replace` keeps the dead id out of the
  // history stack, which matters inside the dealer iframe where Back is bridged to the host page.
  useEffect(() => {
    if (!fallbackStoreId) {
      return;
    }

    history.replace(
      createResourceLocatorString(
        'StorePage',
        routeConfiguration,
        { id: fallbackStoreId },
        rawUrlQueryParams
      )
    );
  }, [fallbackStoreId, history, routeConfiguration, rawQueryString]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isInIframe) return;
    try {
      window.sessionStorage.setItem('dealerIframeJourney', '1');
      // Notify same-document listeners (TopbarContainer) synchronously.
      // sessionStorage writes do NOT fire a 'storage' event in the same tab,
      // and TopbarContainer mounts as a child of this page — so its palette
      // effect runs BEFORE this write. The event lets it re-read the marker
      // right after we set it, so the store landing itself turns gray on the
      // very first load instead of only on the next navigation.
      window.dispatchEvent(new Event('dealerIframeJourney:update'));
    } catch (e) {}
  }, [isInIframe]);

  useEffect(() => {
    props.handleDomLoaded();
  }, []);

  useEffect(() => {
    if (props.domLoaded && (props?.listings || []).length && !props.firstLoaded) {
      props.handleFirstLoaded();
      const creatorsId = (props?.listings || [])
        .map(l => l?.attributes?.publicData?.creatorId)
        .filter(l => l);
      props.handleCreators(creatorsId);
    }
  }, [props?.listings]);

  // Auto-resize iframe container
  useEffect(() => {
    const sendHeightToParent = () => {
      setTimeout(() => {
        const fullHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        window.parent.postMessage(
          {
            type: 'bowtackle-resize',
            height: fullHeight,
          },
          '*'
        );
      }, 1000);
    };

    sendHeightToParent();

    const observer = new MutationObserver(sendHeightToParent);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => observer.disconnect();
  }, [props?.listings, props.domLoaded]);

  // Keep the iframe search input synchronized with the URL.
  useEffect(() => {
    setSearchKeyword(urlQueryParams.keywords || '');
  }, [urlQueryParams.keywords]);

  useEffect(() => {
    // While a fallback redirect is pending this would rewrite the URL back to the store we are
    // leaving, since `props.params.id` still holds the id that failed.
    if (fallbackStoreId) {
      return;
    }

    if (rawQueryString !== sanitizedQueryString) {
      history.replace(
        createResourceLocatorString(
          'StorePage',
          routeConfiguration,
          { id: props.params.id },
          urlQueryParams
        )
      );
    }
  }, [
    fallbackStoreId,
    history,
    props.params.id,
    rawQueryString,
    routeConfiguration,
    sanitizedQueryString,
    urlQueryParams,
  ]);

  // Re-fetch listings when URL search params change (e.g. sidebar filter changes)
  useEffect(() => {
    if (props.params?.id) {
      handleSearch(props.params, urlQueryParams.keywords || null, config, sanitizedSearch);
    }
  }, [config, handleSearch, props.params, sanitizedSearch, urlQueryParams.keywords]);

  useEffect(() => {
    setCurrentPage(1);
  }, [location.search]);

  // ========== Filter logic ==========

  // Get current sort value from URL
  const currentSort = urlQueryParams.sort || '';

  // Navigate to updated URL with new params
  const pushToUrl = useCallback(
    newParams => {
      // Merge with existing params, remove nulls
      const merged = { ...urlQueryParams, ...newParams };
      const cleaned = sanitizeTaxonomyFilterParams(
        Object.fromEntries(Object.entries(merged).filter(([_, v]) => v != null && v !== ''))
      );
      history.push(
        createResourceLocatorString(
          'StorePage',
          routeConfiguration,
          { id: props.params.id },
          cleaned
        )
      );
    },
    [urlQueryParams, history, routeConfiguration, props.params.id]
  );

  // Category tab click handler (used by breadcrumbs and "View All")
  const setCategory = selectedCategory => {
    const newParams = { ...urlQueryParams };
    if (selectedCategory === 'all') {
      delete newParams.pub_category;
      delete newParams.pub_subcategory;
    } else {
      newParams.pub_category = selectedCategory;
      delete newParams.pub_subcategory;
    }
    const cleaned = Object.fromEntries(
      Object.entries(newParams).filter(([_, v]) => v != null && v !== '')
    );
    history.push(
      createResourceLocatorString('StorePage', routeConfiguration, { id: props.params.id }, cleaned)
    );
  };

  // Category tab click — filters immediately (no subcategory dropdown)
  const handleCategoryTabClick = categoryOption => {
    setCategory(categoryOption);
  };

  // Sort change handler
  const handleSortChange = e => {
    const value = e.target.value;
    if (value) {
      pushToUrl({ sort: value });
    } else {
      pushToUrl({ sort: null });
    }
  };

  // Keyword search handler
  const handleKeywordSearch = e => {
    e.preventDefault();
    pushToUrl({ keywords: searchKeyword || null });
  };

  // Reset all filters
  const resetAll = () => {
    // Keep only iframe param if present
    const iframeMaybe = urlQueryParams.iframe ? { iframe: urlQueryParams.iframe } : {};
    history.push(
      createResourceLocatorString(
        'StorePage',
        routeConfiguration,
        { id: props.params.id },
        iframeMaybe
      )
    );
  };

  // Determine heading label
  const currentCategoryLabel =
    currentCategory && currentCategory !== 'all'
      ? categories.find(c => c.option === currentCategory)?.label || currentCategory
      : 'All Products';

  const currentSubcategoryLabel =
    currentSubcategory && currentSubcategory !== 'all'
      ? subcategories.find(c => c.option === currentSubcategory)?.label || currentSubcategory
      : null;
  const currentChannel = urlQueryParams.channel === 'warehouse' ? 'warehouse' : 'in-store';
  const handleChannelChange = next =>
    pushToUrl({ channel: next === 'warehouse' ? 'warehouse' : null });

  // Client-side filtering for fields without Sharetribe search indexes
  const listingsMatchingContext = listings.filter(l => {
    const pd = l?.attributes?.publicData || {};

    const conditionFilter = urlQueryParams.pub_condition;
    if (conditionFilter) {
      const selected = String(conditionFilter).split(',');
      if (!listingMatchesCondition(pd, selected)) return false;
    }

    const sourceFilter = urlQueryParams.pub_source;
    if (sourceFilter) {
      const selected = String(sourceFilter).split(',');
      if (!selected.includes(pd.source)) return false;
    }

    const channelFilter = urlQueryParams.channel;
    if (isWarehouseChannel(channelFilter) && !isWarehouseSource(pd.source)) {
      return false;
    }
    if (!isWarehouseChannel(channelFilter) && isWarehouseSource(pd.source)) {
      return false;
    }

    const manufacturerFilter = urlQueryParams.pub_isManufacturer;
    if (manufacturerFilter != null) {
      const selected = String(manufacturerFilter).split(',');
      if (!selected.includes(String(!!pd.isManufacturer))) return false;
    }

    // Category filter (client-side fallback in case pub_category is not indexed)
    const categoryFilter = urlQueryParams.pub_category;
    if (categoryFilter && categoryFilter !== 'all') {
      const selected = String(categoryFilter).split(',');
      if (!selected.includes(pd.category)) return false;
    }

    // Subcategory filter (client-side fallback in case pub_subcategory is not indexed)
    const subcategoryFilter = urlQueryParams.pub_subcategory;
    if (subcategoryFilter && subcategoryFilter !== 'all') {
      const selected = String(subcategoryFilter)
        .split(',')
        .map(value => normalizeLegacySubcategorySlug(value));
      const listingSubcategory = normalizeLegacySubcategorySlug(pd.subcategory);
      if (!selected.includes(listingSubcategory)) return false;
    }

    // Price filter: URL param is in major units (dollars), listing price is in subunits (cents)
    const priceFilter = urlQueryParams.price;
    if (priceFilter) {
      const [minDollars, maxDollars] = String(priceFilter)
        .split(',')
        .map(Number);
      const listingCents = l?.attributes?.price?.amount;
      if (listingCents == null) return false;
      if (listingCents < minDollars * 100 || listingCents > maxDollars * 100) return false;
    }

    // Delivery option filter: prefer canonical deliveryOption, fallback to booleans only if missing.
    const deliveryFilter = urlQueryParams.pub_deliveryOption;
    if (deliveryFilter) {
      const selected = parseSelectFilterOptions(deliveryFilter);
      if (!listingMatchesDeliveryOptions(pd, selected)) return false;
    }

    return true;
  });
  const availableBrandValues = getAvailableBrandValues(listingsMatchingContext);
  const filteredListings = filterListingsByBrand(listingsMatchingContext, urlQueryParams.pub_brand);
  const totalPages = Math.max(1, Math.ceil(filteredListings.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedListings = filteredListings.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  const handleBrandSelect = name => {
    setCurrentPage(1);
    pushToUrl({ pub_brand: name });
  };

  // ========== Render ==========
  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'StorePage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={
          isInIframe ? (
            <TopbarContainer
              iframeCategories={categories}
              iframeCurrentCategory={currentCategory}
              onIframeCategoryClick={handleCategoryTabClick}
              iframeSearchKeyword={searchKeyword}
              setIframeSearchKeyword={setSearchKeyword}
              onIframeSearchSubmit={handleKeywordSearch}
              setMobileMenuOpen={setMobileMenuOpen}
              showMobileItemSourceToggle
              mobileItemSourceValue={currentChannel}
              onMobileItemSourceChange={handleChannelChange}
              hideMobileSearchBanner
            />
          ) : (
            <></>
          )
        }
        footer={null}
      >
        <CatalogSearchContent
          categories={categories}
          currentCategory={currentCategory}
          currentCategoryLabel={currentCategoryLabel}
          currentSort={currentSort}
          currentSubcategoryLabel={currentSubcategoryLabel}
          fetchingListings={fetchingListings}
          intl={intl}
          listings={pagedListings}
          manufacturerImages={manufacturerImages}
          manufacturerImagesInProgress={manufacturerImagesInProgress}
          mobileMenuOpen={mobileMenuOpen}
          onCategoryClick={setCategory}
          onParamChange={pushToUrl}
          onResetAll={resetAll}
          onSortChange={handleSortChange}
          showMobileTopbarSourceToggle
          showMobileBrandBeforeControls
          showMobileInlineSearch
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          onSearchSubmit={handleKeywordSearch}
          searchPlaceholder="Tell us more about what you're looking for..."
          showSourceToggle
          resultsFooter={
            <SearchPagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          }
          setMobileMenuOpen={setMobileMenuOpen}
          sortOptions={[{ key: '', label: 'Sort By' }, ...SORT_OPTIONS]}
          urlQueryParams={urlQueryParams}
          brands={BRANDS}
          onBrandSelect={handleBrandSelect}
          availableBrandValues={availableBrandValues}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

StorePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  creators: [],
  queryReviewsError: null,
};

StorePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, creators } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    categories,
    subcategories,
    currentCategory,
    currentSubcategory,
    fetchingListings,
    manufacturerImages,
    manufacturerImagesInProgress,
    manufacturerImagesError,
    fallbackStoreId,
  } = state.StorePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);

  const { domLoaded, firstLoaded } = state.ui;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    categories,
    subcategories,
    currentCategory,
    currentSubcategory,
    fetchingListings,
    creators,
    domLoaded,
    firstLoaded,
    manufacturerImages,
    manufacturerImagesInProgress,
    manufacturerImagesError,
    fallbackStoreId,
  };
};

const mapDispatchToProps = dispatch => ({
  handleDomLoaded: () => dispatch(setDOMLoaded()),
  handleFirstLoaded: () => dispatch(setFirstLoaded()),
  handleCreators: creatorsId => dispatch(fetchCreators(creatorsId)),
  handleSearch: (params, keywords, config, search) =>
    dispatch(reloadListings(params, keywords, config, search)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const StorePage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(StorePageComponent);

export default StorePage;
